import { useContext, useEffect } from "react"
import classNames from "classnames"

import { RecordContext } from "web/contexts"
import { convertHTMLToPlainText, isNotAValue } from "web/services/helpers"

import ControlContainer from "../ControlContainer"
import useAsyncRichTextModal from "./AsyncRichTextModal"
import useRichTextModal from "./RichTextModal"

import { DocumentTextIcon } from "@heroicons/react/24/outline"

export default function RichTextPreview({
  fieldKey,
  value,
  label,
  viewMode = false,
  inert = false,
  readOnly = false,
  hideLabel = false,
  onChange,
  autoFocus = false,
  preventClickHandler = false,
}) {
  const record = useContext(RecordContext)
  const recordSaved = !!record
  const canEditRecord =
    !readOnly && recordSaved && record.user_permissions.update

  const showModal = useRichTextModal({
    label,
    value: value?.value,
  })

  const showAsyncModal = useAsyncRichTextModal({
    fieldKey,
    label,
    record,
    readOnly: !canEditRecord,
  })

  async function openModel() {
    if (recordSaved) {
      showAsyncModal()
    } else {
      const response = await showModal()
      if (response === "CLEAR") {
        onChange(null)
      } else if (!!response) {
        onChange({ value: response })
      }
    }
  }

  async function handleClick(e) {
    e.stopPropagation()
    if (!inert) {
      openModel()
    }
  }

  const noValue = isNotAValue(value?.value)

  const textValue = value?.value

  const preview = noValue
    ? "Click to edit..."
    : convertHTMLToPlainText(textValue)

  const classes = classNames(
    "edit-mode-and-click-on-enter-key truncate w-full",
    {
      "flex box-border text-ellipsis border border-neutral-8 p-2.5": !viewMode,
      "hover:text-primary-4 hover:bg-primary-10 hover:rounded hover:px-2 cursor-pointer":
        viewMode && !inert,
      "text-gray-500": noValue,
    }
  )

  useEffect(() => {
    if (autoFocus) {
      openModel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ControlContainer
      label={label}
      hideLabel={viewMode || hideLabel}
      className="overflow-hidden"
    >
      <div
        className={classes}
        onClick={preventClickHandler ? undefined : handleClick}
      >
        <div className="mr-1 h-6 flex-auto truncate leading-6">{preview}</div>
        {!viewMode && (
          <div>
            <DocumentTextIcon className="h-5 w-5" alt="view note" />
          </div>
        )}
      </div>
    </ControlContainer>
  )
}
